import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, Button, Alert, Chip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import SquareIcon from '@mui/icons-material/Square'
import HighlightAltIcon from '@mui/icons-material/HighlightAlt'
import mapboxgl from 'mapbox-gl'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN

/**
 * Normaliza cualquier tipo de GeoJSON a FeatureCollection para Mapbox
 */
const normalizeForMapbox = geojson => {
  if (!geojson) return null
  if (geojson.type === 'FeatureCollection') return geojson
  if (geojson.type === 'Feature') {
    return { type: 'FeatureCollection', features: [geojson] }
  }
  if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: geojson, properties: {} }],
    }
  }
  return null
}

/**
 * Calcula el bounding box de un GeoJSON
 */
const getBBox = geojson => {
  let coords = []

  const extractCoords = geometry => {
    if (!geometry) return
    switch (geometry.type) {
      case 'Polygon':
        geometry.coordinates[0].forEach(c => coords.push(c))
        break
      case 'MultiPolygon':
        geometry.coordinates.forEach(p => p[0].forEach(c => coords.push(c)))
        break
      case 'Point':
        coords.push(geometry.coordinates)
        break
      default:
        break
    }
  }

  if (geojson.type === 'FeatureCollection') {
    geojson.features.forEach(f => extractCoords(f.geometry))
  } else if (geojson.type === 'Feature') {
    extractCoords(geojson.geometry)
  } else {
    extractCoords(geojson)
  }

  if (coords.length === 0) return null

  const lngs = coords.map(c => c[0])
  const lats = coords.map(c => c[1])

  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ]
}

/**
 * Agrega o actualiza la capa de referencia del cantón en el mapa (no editable)
 */
const applyReferenceLayer = (map, geojson) => {
  if (!map || !geojson) return
  try {
    const normalized = normalizeForMapbox(geojson)
    if (!normalized) return

    if (map.getSource('canton-reference')) {
      map.getSource('canton-reference').setData(normalized)
      return
    }

    map.addSource('canton-reference', { type: 'geojson', data: normalized })

    // Insertar debajo de las capas de dibujo
    const firstDrawLayer = map.getStyle().layers.find(l => l.id.startsWith('gl-draw'))
    const beforeId = firstDrawLayer?.id

    map.addLayer(
      {
        id: 'canton-reference-fill',
        type: 'fill',
        source: 'canton-reference',
        paint: { 'fill-color': '#ff9800', 'fill-opacity': 0.12 },
      },
      beforeId,
    )

    map.addLayer(
      {
        id: 'canton-reference-line',
        type: 'line',
        source: 'canton-reference',
        paint: {
          'line-color': '#e65100',
          'line-width': 2.5,
          'line-dasharray': [4, 2],
        },
      },
      beforeId,
    )
  } catch (err) {
    console.error('Error al agregar capa de referencia del cantón:', err)
  }
}

/**
 * Vuela o centra el mapa en el cantón de referencia.
 * Usa flyTo con center+zoom si están disponibles; de lo contrario usa fitBounds.
 */
const flyToReference = (map, center, zoom, geojson) => {
  if (center && zoom) {
    map.flyTo({ center, zoom, duration: 1200 })
  } else if (geojson) {
    const bbox = getBBox(geojson)
    if (bbox) map.fitBounds(bbox, { padding: 60 })
  }
}

/**
 * Elimina la capa de referencia del cantón del mapa.
 */
const removeReferenceLayer = map => {
  if (!map) return
  try {
    if (map.getLayer('canton-reference-fill')) map.removeLayer('canton-reference-fill')
    if (map.getLayer('canton-reference-line')) map.removeLayer('canton-reference-line')
    if (map.getSource('canton-reference')) map.removeSource('canton-reference')
  } catch (err) {
    console.error('Error al eliminar capa de referencia del cantón:', err)
  }
}

/**
 * Componente para dibujar polígonos en un mapa Mapbox.
 * Muestra el boundary del cantón como capa de referencia no editable.
 */
const MapDrawer = ({
  onDrawComplete,
  initialValue,
  referenceGeoJSON,
  referenceCenter,
  referenceZoom,
}) => {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const drawRef = useRef(null)
  const [hasDrawing, setHasDrawing] = useState(false)

  // Refs para acceder siempre a los valores más recientes sin que el useEffect
  // principal necesite re-ejecutarse
  const referenceGeoJSONRef = useRef(referenceGeoJSON)
  const referenceCenterRef = useRef(referenceCenter)
  const referenceZoomRef = useRef(referenceZoom)
  useEffect(() => {
    referenceGeoJSONRef.current = referenceGeoJSON
  }, [referenceGeoJSON])
  useEffect(() => {
    referenceCenterRef.current = referenceCenter
  }, [referenceCenter])
  useEffect(() => {
    referenceZoomRef.current = referenceZoom
  }, [referenceZoom])

  // Cuando referenceGeoJSON cambia: limpiar el dibujo actual y actualizar la capa
  // de referencia. Si el cantón se deselecciona (null), eliminar la capa también.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const apply = () => {
      // Limpiar el dibujo anterior siempre que cambie el cantón de referencia
      if (drawRef.current) {
        drawRef.current.deleteAll()
        setHasDrawing(false)
        onDrawComplete('')
      }

      if (!referenceGeoJSON) {
        removeReferenceLayer(map)
        return
      }

      applyReferenceLayer(map, referenceGeoJSON)
      flyToReference(
        map,
        referenceCenterRef.current,
        referenceZoomRef.current,
        referenceGeoJSON,
      )
    }

    if (map.isStyleLoaded()) {
      apply()
    } else {
      map.once('load', apply)
      return () => map.off('load', apply)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceGeoJSON])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-79.2, -4.0],
      zoom: 8,
    })

    map.addControl(new mapboxgl.NavigationControl(), 'top-right')

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
      },
      defaultMode: 'draw_polygon',
      styles: [
        {
          id: 'gl-draw-polygon-fill',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon']],
          paint: {
            'fill-color': '#1976d2',
            'fill-outline-color': '#1976d2',
            'fill-opacity': 0.3,
          },
        },
        {
          id: 'gl-draw-polygon-stroke',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon']],
          paint: {
            'line-color': '#1976d2',
            'line-width': 3,
          },
        },
        {
          id: 'gl-draw-point',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point']],
          paint: {
            'circle-radius': 6,
            'circle-color': '#1976d2',
          },
        },
      ],
    })

    map.addControl(draw)

    const updateGeoJSON = () => {
      const data = draw.getAll()
      if (data.features.length > 0) {
        setHasDrawing(true)
        const geojson = data.features.length === 1 ? data.features[0] : data
        onDrawComplete(geojson)
      } else {
        setHasDrawing(false)
        onDrawComplete('')
      }
    }

    const handleDrawCreate = () => updateGeoJSON()
    const handleDrawUpdate = () => updateGeoJSON()
    const handleDrawDelete = () => {
      setHasDrawing(false)
      onDrawComplete('')
    }

    map.on('draw.create', handleDrawCreate)
    map.on('draw.update', handleDrawUpdate)
    map.on('draw.delete', handleDrawDelete)

    map.on('load', () => {
      // Mostrar el boundary del cantón como referencia no editable
      if (referenceGeoJSONRef.current) {
        applyReferenceLayer(map, referenceGeoJSONRef.current)
        flyToReference(
          map,
          referenceCenterRef.current,
          referenceZoomRef.current,
          referenceGeoJSONRef.current,
        )
      }

      // Cargar el polígono existente (modo edición)
      if (initialValue) {
        try {
          const parsed =
            typeof initialValue === 'string' ? JSON.parse(initialValue) : initialValue

          if (parsed.type === 'FeatureCollection') {
            draw.add(parsed)
          } else if (parsed.type === 'Feature') {
            draw.add(parsed)
          } else if (parsed.type === 'Polygon' || parsed.type === 'MultiPolygon') {
            draw.add({ type: 'Feature', geometry: parsed, properties: {} })
          }

          setHasDrawing(true)

          // Centrar en el boundary existente solo si no hay referencia de cantón
          if (!referenceGeoJSONRef.current) {
            const bbox = getBBox(parsed)
            if (bbox) map.fitBounds(bbox, { padding: 50 })
          }
        } catch (err) {
          console.error('Error cargando GeoJSON inicial:', err)
        }
      }
    })

    mapRef.current = map
    drawRef.current = draw

    return () => {
      try {
        if (map) {
          map.off('draw.create', handleDrawCreate)
          map.off('draw.update', handleDrawUpdate)
          map.off('draw.delete', handleDrawDelete)
        }
        if (drawRef.current && map) {
          try {
            map.removeControl(drawRef.current)
          } catch (e) {
            /* ignorar */
          }
        }
        if (mapRef.current) {
          try {
            mapRef.current.remove()
          } catch (e) {
            /* ignorar */
          }
        }
      } catch (e) {
        console.warn('MapDrawer cleanup warning:', e)
      } finally {
        mapRef.current = null
        drawRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClearAll = () => {
    if (drawRef.current) {
      drawRef.current.deleteAll()
      setHasDrawing(false)
      onDrawComplete('')
    }
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Dibuja el boundary dentro del límite del cantón. Haz clic para agregar puntos y
        doble clic para cerrar el polígono.
      </Alert>

      {/* Leyenda */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          icon={<HighlightAltIcon sx={{ color: '#e65100 !important', fontSize: 14 }} />}
          label="Límite de referencia"
          variant="outlined"
          sx={{
            borderColor: '#e65100',
            color: '#e65100',
            fontSize: 12,
            fontWeight: 500,
          }}
        />
        <Chip
          size="small"
          icon={<SquareIcon sx={{ color: '#1976d2 !important', fontSize: 14 }} />}
          label="Boundary a dibujar"
          variant="outlined"
          sx={{ borderColor: '#1976d2', color: '#1976d2', fontSize: 12 }}
        />
      </Box>

      <Box
        ref={mapContainerRef}
        sx={{
          width: '100%',
          height: 400,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {hasDrawing
            ? '✓ Polígono de parroquia dibujado'
            : 'Dibuja el polígono dentro del cantón seleccionado'}
        </Typography>

        {hasDrawing && (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleClearAll}>
            Limpiar dibujo
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default MapDrawer
